/**
 * Blog Data Hooks
 *
 * React Query hooks for fetching blog data from the oRPC API.
 * Query options are exported separately for SSR prefetching in route loaders.
 *
 * Taxonomy: Tool → Topic → Post. URLs are /blog/<tool>/<topic>/<slug>.
 */

import { queryOptions, useQuery } from "@tanstack/react-query";
import { orpc } from "@/orpc/client";

// ============================================================================
// Types
// ============================================================================

export interface BlogPostSummary {
  id: string;
  publishedAt: string | null;
  coverImage: string | null;
  coverImageMobile: string | null;
  readingTimeMinutes: number;
  slug: string;
  title: string;
  description: string;
  toolSlug: string;
  toolName: string;
  topicSlug: string;
  topicName: string;
}

export interface BlogPostTag {
  slug: string;
  name: string;
}

export interface BlogPost {
  id: string;
  status: string;
  publishedAt: string | null;
  updatedAt: string | null;
  coverImage: string | null;
  coverImageMobile: string | null;
  readingTimeMinutes: number;
  chartData: string | null;
  title: string;
  description: string;
  html: string;
  toc: string;
  slugs: Record<string, string | null>;
  toolName: string;
  toolDefaultSlug: string;
  toolSlug: string;
  toolSlugs: Record<string, string | null>;
  topicName: string;
  topicDefaultSlug: string;
  topicSlug: string;
  topicSlugs: Record<string, string | null>;
  tags: BlogPostTag[];
}

export interface BlogTopicSummary {
  defaultSlug: string;
  name: string;
  description: string;
  slug: string;
}

export interface BlogTool {
  id: string;
  defaultSlug: string;
  name: string;
  description: string;
  slug: string;
}

export interface BlogToolDetail extends BlogTool {
  slugs: Record<string, string | null>;
  topics: BlogTopicSummary[];
}

export interface BlogTopicDetail {
  id: string;
  defaultSlug: string;
  name: string;
  description: string;
  slug: string;
  slugs: Record<string, string | null>;
  toolName: string;
  toolSlug: string;
  toolSlugs: Record<string, string | null>;
}

// ============================================================================
// Query Options (for SSR prefetching in route loaders)
// ============================================================================

export const blogPostQueryOptions = (
  toolSlug: string,
  topicSlug: string,
  slug: string,
  locale: string,
) =>
  queryOptions({
    queryKey: ["blog", "post", toolSlug, topicSlug, slug, locale] as const,
    queryFn: () =>
      orpc.blog.getPost.call({ toolSlug, topicSlug, slug, locale }),
    staleTime: 1000 * 60 * 10,
  });

/** Resolve a post's canonical tool/topic from its slug — for 301 redirects. */
export const blogPostLocationQueryOptions = (slug: string, locale: string) =>
  queryOptions({
    queryKey: ["blog", "postLocation", slug, locale] as const,
    queryFn: () => orpc.blog.getPostLocation.call({ slug, locale }),
    staleTime: 1000 * 60 * 10,
  });

/** Posts shown per page on the blog index, tool, and topic listings. */
export const POSTS_PER_PAGE = 12;

/** A page of posts plus the total count (for computing the page count). */
export interface BlogPostsPage {
  items: BlogPostSummary[];
  total: number;
}

/**
 * Range of posts to fetch for a page. A `featured` listing (the blog index)
 * promotes the first post to a hero, so page 1 fetches ONE extra post to keep
 * the grid below it full (a 3-column grid needs a multiple of 3, not 11); later
 * pages shift their offset by that extra slot.
 */
function listRange(
  page: number,
  featured: boolean,
): { limit: number; offset: number } {
  if (featured) {
    return page <= 1
      ? { limit: POSTS_PER_PAGE + 1, offset: 0 }
      : {
          limit: POSTS_PER_PAGE,
          offset: POSTS_PER_PAGE + 1 + (page - 2) * POSTS_PER_PAGE,
        };
  }
  return { limit: POSTS_PER_PAGE, offset: (page - 1) * POSTS_PER_PAGE };
}

/** Number of pages for `total` posts, accounting for the featured slot. */
export function blogPageCount(total: number, featured = false): number {
  if (total <= 0) return 0;
  if (!featured) return Math.ceil(total / POSTS_PER_PAGE);
  if (total <= POSTS_PER_PAGE + 1) return 1;
  return 1 + Math.ceil((total - POSTS_PER_PAGE - 1) / POSTS_PER_PAGE);
}

export const blogPostsQueryOptions = (
  locale: string,
  toolSlug?: string,
  topicSlug?: string,
  page = 1,
  featured = false,
) => {
  const { limit, offset } = listRange(page, featured);
  return queryOptions({
    queryKey: [
      "blog",
      "posts",
      locale,
      toolSlug ?? "all",
      topicSlug ?? "all",
      page,
      featured,
    ] as const,
    queryFn: () =>
      orpc.blog.listPosts.call({ locale, toolSlug, topicSlug, limit, offset }),
    staleTime: 1000 * 60 * 5,
  });
};

export const blogToolsQueryOptions = (locale: string) =>
  queryOptions({
    queryKey: ["blog", "tools", locale] as const,
    queryFn: () => orpc.blog.listTools.call({ locale }),
    staleTime: 1000 * 60 * 60,
  });

export const blogToolQueryOptions = (slug: string, locale: string) =>
  queryOptions({
    queryKey: ["blog", "tool", slug, locale] as const,
    queryFn: () => orpc.blog.getTool.call({ slug, locale }),
    staleTime: 1000 * 60 * 60,
  });

export const blogTopicQueryOptions = (
  toolSlug: string,
  topicSlug: string,
  locale: string,
) =>
  queryOptions({
    queryKey: ["blog", "topic", toolSlug, topicSlug, locale] as const,
    queryFn: () => orpc.blog.getTopic.call({ toolSlug, topicSlug, locale }),
    staleTime: 1000 * 60 * 60,
  });

export const blogPostsByTagQueryOptions = (
  tagSlug: string,
  locale: string,
  limit = 200,
  offset = 0,
) =>
  queryOptions({
    queryKey: ["blog", "postsByTag", tagSlug, locale, limit, offset] as const,
    queryFn: () =>
      orpc.blog.listPostsByTag.call({ tagSlug, locale, limit, offset }),
    staleTime: 1000 * 60 * 5,
  });

export const blogTagQueryOptions = (slug: string, locale: string) =>
  queryOptions({
    queryKey: ["blog", "tag", slug, locale] as const,
    queryFn: () => orpc.blog.getTag.call({ slug, locale }),
    staleTime: 1000 * 60 * 60,
  });

export const blogPostSlugsQueryOptions = () =>
  queryOptions({
    queryKey: ["blog", "postSlugs"] as const,
    queryFn: () => orpc.blog.getPostSlugs.call({}),
    staleTime: 1000 * 60 * 60,
  });

// ============================================================================
// Hooks
// ============================================================================

export function useBlogPost(
  toolSlug: string | undefined,
  topicSlug: string | undefined,
  slug: string | undefined,
  locale: string,
) {
  return useQuery({
    ...blogPostQueryOptions(
      toolSlug ?? "",
      topicSlug ?? "",
      slug ?? "",
      locale,
    ),
    enabled: !!toolSlug && !!topicSlug && !!slug,
  });
}

export function useBlogPosts(
  locale: string,
  toolSlug?: string,
  topicSlug?: string,
  page = 1,
  featured = false,
) {
  return useQuery(
    blogPostsQueryOptions(locale, toolSlug, topicSlug, page, featured),
  );
}

export function useBlogTools(locale: string) {
  return useQuery(blogToolsQueryOptions(locale));
}

export function useBlogTool(slug: string | undefined, locale: string) {
  return useQuery({
    ...blogToolQueryOptions(slug ?? "", locale),
    enabled: !!slug,
  });
}

export function useBlogTopic(
  toolSlug: string | undefined,
  topicSlug: string | undefined,
  locale: string,
) {
  return useQuery({
    ...blogTopicQueryOptions(toolSlug ?? "", topicSlug ?? "", locale),
    enabled: !!toolSlug && !!topicSlug,
  });
}

export function useBlogPostsByTag(
  tagSlug: string | undefined,
  locale: string,
  limit = 200,
  offset = 0,
) {
  return useQuery({
    ...blogPostsByTagQueryOptions(tagSlug ?? "", locale, limit, offset),
    enabled: !!tagSlug,
  });
}

export function useBlogTag(slug: string | undefined, locale: string) {
  return useQuery({
    ...blogTagQueryOptions(slug ?? "", locale),
    enabled: !!slug,
  });
}
